import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";

const TT_FEE = "From $2,200" as const;

const FAQS = [
  {
    q: "What is a testamentary trust?",
    a: "It is a trust created by your will that only comes into existence when you die. Instead of a beneficiary receiving their inheritance outright, it is held in a trust they usually control, and income and capital are distributed from it. It is a will structure, not a separate trust you set up now.",
  },
  {
    q: "What is a testamentary discretionary trust?",
    a: "The most common kind of testamentary trust. The trustee, usually the beneficiary themselves, decides each year who in the family receives income and capital. That flexibility is what gives the tax and asset-protection benefits. A protective trust is the other main type: an independent trustee controls it for a vulnerable beneficiary.",
  },
  {
    q: "How much does a testamentary trust will cost?",
    a: `${TT_FEE} including GST for a will containing a testamentary trust. That is the fixed fee, quoted before we start, and it covers taking your instructions, advising on the structure, drafting and executing the will. Complex estates — blended families, business or company and trust interests, self managed super funds, rural land or overseas assets — are quoted individually before we begin.`,
  },
  {
    q: "What is the tax advantage?",
    a: "Income distributed from a testamentary trust to a beneficiary's minor children is taxed at ordinary adult marginal rates with the full tax-free threshold, rather than the punitive minor rates that apply to ordinary trust distributions. For a family with young children inheriting an income-producing estate, that can be a meaningful saving every year the trust runs. Anti-avoidance rules limit the concession to assets that actually came from the estate — we explain the boundaries.",
  },
  {
    q: "Who is it actually worth it for?",
    a: "Broadly: estates with real assets producing income, beneficiaries with young children, a child in a shaky marriage or with creditor exposure, a beneficiary with a disability or who cannot manage money, a blended family where you want to provide for a spouse but protect the children of a first relationship, and business or investment assets you want kept intact. If the estate is a modest home and some super going to two independent adults, an ordinary will is usually the right answer and we will say so.",
  },
  {
    q: "Does it protect the inheritance in a divorce?",
    a: "It helps, but no structure is bulletproof. Family courts can look at what a party controls and what they receive. A trust that is genuinely discretionary, with the right person as appointor, keeps the inheritance out of the pool far better than money paid into a joint account — which is what happens with an ordinary will.",
  },
  {
    q: "What is the difference between a testamentary trust and a family trust?",
    a: "A family (discretionary) trust is set up while you are alive, holds assets now, and has ongoing costs and compliance from day one. A testamentary trust exists only in the wording of your will until you die, costs nothing to maintain in the meantime, and carries the minor-beneficiary tax concession that a family trust does not.",
  },
  {
    q: "What are the downsides?",
    a: "The trust has to be administered — a tax file number, an annual return, trustee resolutions before 30 June, and accounting fees each year. Beneficiaries who want the money in their own name can find it irritating. It is a live structure, not a set-and-forget clause, and it should only go in the will if the benefit outweighs that ongoing work.",
  },
  {
    q: "Who controls the trust after I die?",
    a: "Typically the beneficiary is the trustee of their own trust and can appoint income and capital among a class of family members. You can instead appoint an independent trustee, or split the roles by naming an appointor with the power to remove the trustee — which is the usual approach where a beneficiary is vulnerable, addicted or under external pressure.",
  },
  {
    q: "Can I add one to my existing will?",
    a: "Generally the will is redrawn rather than amended by codicil, because the trust provisions interact with the whole document. It is one appointment and the same fixed fee.",
  },
];

const WHO = [
  {
    t: "Beneficiaries with young children",
    d: "The single clearest financial case. Trust income can be streamed to the grandchildren at adult marginal rates with the full tax-free threshold each — school fees paid substantially out of pre-tax income.",
  },
  {
    t: "A child whose marriage is uncertain",
    d: "Money paid to a child outright is money in the matrimonial pool. Held in a properly structured discretionary trust, it is much harder to reach.",
  },
  {
    t: "Professionals and business owners",
    d: "Directors, builders, medicos and anyone carrying personal guarantee or negligence exposure. An inheritance sitting in their own name is exposed to their creditors; a trust interest is not owned in the same way.",
  },
  {
    t: "Blended families",
    d: "Provide for your spouse for life while ensuring what is left passes to the children of your first relationship, rather than being redirected by a later will they make.",
  },
  {
    t: "A vulnerable or disabled beneficiary",
    d: "A protective trust with an independent trustee, drafted with an eye to the beneficiary's pension and support entitlements.",
  },
  {
    t: "A beneficiary who cannot hold money",
    d: "Gambling, addiction, or persistent pressure from a partner. Capital stays in the trust and is released as needed rather than handed over in one line.",
  },
];

export const Route = createFileRoute("/testamentary-trusts")({
  head: () => ({
    meta: [
      { title: fitTitle("Testamentary Trust QLD: What It Is & Fixed Fee From $2,200") },
      {
        name: "description",
        content:
          fitDescription("Testamentary trust QLD guide: what a testamentary trust is, the tax and asset protection it gives, who needs one, and our fixed fee for a testamentary trust will from $2,200 incl GST."),
      },
      { property: "og:title", content: fitTitle(`Testamentary Trust Wills | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("What a testamentary trust does, who it suits, what it costs to run, and our fixed fee to put one in your will."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/testamentary-trusts" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/testamentary-trusts" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: TestamentaryTrustsPage,
});

function TestamentaryTrustsPage() {
  return (
    <>
      <PageHero
        eyebrow="Wills & estates"
        title="Testamentary trust QLD: wills with a testamentary trust"
        cta="Ask about a testamentary trust will"
        lead="What is a testamentary trust? In QLD it is a trust created by your will, and it's often part of good estate planning. Instead of an inheritance landing in a beneficiary's own name, it is held in a trust they usually control — which changes how it is taxed, and how exposed it is to a divorce, a creditor or a bad decision."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border border-border bg-card p-7">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <h2 className="font-serif text-2xl text-foreground">Will with a testamentary trust</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Fixed fee including GST, quoted and confirmed before we start
              </p>
            </div>
            <span className="font-serif text-3xl text-primary">{TT_FEE}*</span>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            Covers the appointment, advice on whether the structure is worth it for your family, the
            trust design — trustee, appointor, beneficiary class, control on death — drafting and
            signing. If we think you do not need one, we will tell you and draft an ordinary will
            instead.
          </p>
          <p className="mt-4 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
            * Complex estates are quoted individually before we start — blended families, business,
            company or trust interests, self managed super funds, rural land, overseas assets, or
            where a challenge to the will is likely. You will always have the figure in writing
            before we begin any work.
          </p>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Who it is worth it for</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {WHO.map((w) => (
            <div key={w.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-xl text-foreground">{w.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">The honest downside</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            A testamentary trust is a real trust once it starts. It needs a tax file number, an
            annual tax return, trustee resolutions before 30 June each year and an accountant. If
            your estate is a house and some superannuation passing to two capable adults with no
            creditor or relationship risk, the annual cost will outweigh the benefit. We would
            rather tell you that at the appointment than sell you a structure your family later
            unwinds.
          </p>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who you will meet" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-10 text-sm text-muted-foreground">
          New to the idea? Start with{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "what-is-a-testamentary-trust" }}
            className="font-semibold text-primary underline"
          >
            what is a testamentary trust
          </Link>
          , then compare it with an ordinary will in{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "testamentary-trust-vs-simple-will" }}
            className="font-semibold text-primary underline"
          >
            testamentary trust vs simple will
          </Link>
          .
        </p>


        <p className="mt-4 text-sm text-muted-foreground">
          For the detail, read our guide to{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "testamentary-trusts-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            testamentary trusts in Queensland
          </Link>
          , or{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "diy-will-kit-vs-solicitor-drafted-will-queensland" }}
            className="font-semibold text-primary underline"
          >
            why will kits cannot do this
          </Link>
          .
        </p>

        <p className="mt-4 text-sm text-muted-foreground">
          Administering an estate instead? See{" "}
          <Link to="/probate" className="font-semibold text-primary underline">
            probate and estate administration
          </Link>{" "}
          or our full{" "}
          <Link to="/wills-and-estates" className="font-semibold text-primary underline">
            wills and estates
          </Link>{" "}
          service.
        </p>

        <div className="mt-14">
          <EnquiryForm heading="Ask whether a testamentary trust suits your family" />
        </div>
      </section>

      <CtaBand
        title="One appointment, a fixed fee, and a will that actually protects them"
        body={`Call ${FIRM.phone} to book — Golden Beach, Scarborough, by video, or at your home if that is easier.`}
      />
      <div className="h-20" />
    </>
  );
}
